// src/components/Attendance.js
import React, { useState, useEffect } from "react";
import {
  collection,
  getDocs,
  getCountFromServer,
  query,
  where,
  orderBy,
  startAfter,
  limit,
  documentId,
  Timestamp,
} from "firebase/firestore";
import { db } from "../firebase";
import PageHeader from "./ui/PageHeader";
import Button from "./ui/Button";
import { TextField, SelectField, CheckboxField } from "./ui/Field";
import { buildAttendanceRows, toCsv } from "../utils/attendanceCsv";

// 今月を "YYYY-MM" で返す（<input type="month"> の初期値）
const currentMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
};

// 1回の取得件数。全期間でも進捗を細かく出せるよう分割して読む。
const PAGE_SIZE = 1000;

// 指定月の出席記録の条件。month が null なら全期間。
const buildConstraints = (month) => {
  if (!month) return { filters: [], order: orderBy(documentId()) };
  const [year, mon] = month.split("-").map(Number);
  const start = new Date(year, mon - 1, 1);
  const end = new Date(year, mon, 1);
  return {
    filters: [
      where("date", ">=", Timestamp.fromDate(start)),
      where("date", "<", Timestamp.fromDate(end)),
    ],
    order: orderBy("date"),
  };
};

// 出席記録をページ単位で取得し、onProgress(読込済み件数, 総件数) を通知する。
// 校舎は複合インデックス不要にするため取得後に絞り込む。
const fetchRecords = async (month, studioId, onProgress) => {
  const studentsRef = collection(db, "students");
  const { filters, order } = buildConstraints(month);
  const countSnapshot = await getCountFromServer(query(studentsRef, ...filters));
  const total = countSnapshot.data().count;
  onProgress(0, total);

  const fetchPage = async (cursor, acc) => {
    const page = await getDocs(
      query(
        studentsRef,
        ...filters,
        order,
        ...(cursor ? [startAfter(cursor)] : []),
        limit(PAGE_SIZE)
      )
    );
    const loaded = [...acc, ...page.docs.map((d) => d.data())];
    onProgress(loaded.length, Math.max(total, loaded.length));
    return page.size < PAGE_SIZE
      ? loaded
      : fetchPage(page.docs[page.docs.length - 1], loaded);
  };

  const records = await fetchPage(null, []);
  return records.filter((r) => studioId === "all" || r.studio_id === studioId);
};

// 進捗バー
function ProgressBar({ loaded, total }) {
  const percent = total > 0 ? Math.round((loaded / total) * 100) : 0;
  return (
    <div style={{ marginTop: 16 }} aria-live="polite">
      <div
        role="progressbar"
        aria-valuenow={percent}
        aria-valuemin={0}
        aria-valuemax={100}
        style={{
          height: 8,
          borderRadius: 999,
          background: "var(--bg-muted)",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            width: `${percent}%`,
            height: "100%",
            background: "var(--accent)",
            transition: "width 0.2s ease",
          }}
        />
      </div>
      <p
        style={{
          margin: "6px 0 0",
          fontSize: 12,
          color: "var(--text-secondary)",
        }}
      >
        読み込み中 {loaded.toLocaleString()} / {total.toLocaleString()}件（
        {percent}%）
      </p>
    </div>
  );
}

const downloadCsv = (csv, filename) => {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};

function Attendance() {
  const [studios, setStudios] = useState([]);
  const [month, setMonth] = useState(currentMonth());
  const [allPeriod, setAllPeriod] = useState(false);
  const [studioId, setStudioId] = useState("all");
  const [exporting, setExporting] = useState(false);
  const [message, setMessage] = useState("");
  const [progress, setProgress] = useState(null);

  useEffect(() => {
    const fetchStudios = async () => {
      try {
        const snapshot = await getDocs(collection(db, "studios"));
        setStudios(
          snapshot.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .filter((s) => s.display_flg)
        );
      } catch (error) {
        console.error("Error fetching studios: ", error);
      }
    };
    fetchStudios();
  }, []);

  const handleExport = async () => {
    if (!allPeriod && !/^\d{4}-\d{2}$/.test(month)) {
      setMessage("対象月を選択してください");
      return;
    }
    try {
      setExporting(true);
      setMessage("");
      const records = await fetchRecords(
        allPeriod ? null : month,
        studioId,
        (loaded, total) => setProgress({ loaded, total })
      );
      if (records.length === 0) {
        setMessage("該当する受講記録がありません");
        return;
      }
      const studio = studios.find((s) => s.id === studioId);
      const suffix = studio ? `_${studio.name}` : "";
      downloadCsv(
        toCsv(buildAttendanceRows(records)),
        `受講記録_${allPeriod ? "全期間" : month}${suffix}.csv`
      );
      setMessage(`${records.length}件を出力しました`);
    } catch (error) {
      console.error("Error exporting attendance: ", error);
      setMessage("出力に失敗しました。時間をおいて再度お試しください");
    } finally {
      setExporting(false);
      setProgress(null);
    }
  };

  return (
    <div>
      <PageHeader
        title="受講記録"
        subtitle="出席アプリで登録された受講記録をCSVで出力します"
      />

      <div className="card" style={{ padding: 20, maxWidth: 480 }}>
        <TextField
          label="対象月"
          type="month"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          disabled={allPeriod}
        />
        <CheckboxField
          label="全期間を出力する"
          checked={allPeriod}
          onChange={(e) => setAllPeriod(e.target.checked)}
        />
        <SelectField
          label="校舎"
          value={studioId}
          onChange={(e) => setStudioId(e.target.value)}
        >
          <option value="all">すべて</option>
          {studios.map((studio) => (
            <option key={studio.id} value={studio.id}>
              {studio.name}
            </option>
          ))}
        </SelectField>

        <Button variant="primary" onClick={handleExport} disabled={exporting}>
          <i className="ti ti-download" aria-hidden="true" />
          {exporting ? "出力中…" : "CSV出力"}
        </Button>

        {progress && (
          <ProgressBar loaded={progress.loaded} total={progress.total} />
        )}

        {message && (
          <p
            style={{
              margin: "12px 0 0",
              fontSize: 13,
              color: "var(--text-secondary)",
            }}
          >
            {message}
          </p>
        )}
      </div>
    </div>
  );
}

export default Attendance;
